import { db, handleFirestoreError, OperationType } from '../firebase';
import { collection, getDocs, addDoc, query, orderBy, doc, setDoc } from 'firebase/firestore';
import { getCachedQuery, clearDataCache } from './dataCacheService';

export const DEFAULT_ABSENCE_REASONS: string[] = [
  'מחלה',
  'הצהרת מחלה',
  'חופשה',
  'מילואים',
  'אבל',
  'חופשת לידה / הורות',
  'השתלמות / הדרכה',
  'יום בחירה',
  'אירוע משפחתי / שמחה',
  'בדיקות רפואיות',
  'תאונת עבודה',
  'היעדרות מאושרת',
  'אחר'
];

export const DEFAULT_STAFF_ROLES: string[] = [
  'מנהל/ת',
  'סגן/ית מנהל/ת',
  'מורה / מחנך/ת',
  'גננת',
  'סייע/ת',
  'סייע/ת רפואית',
  'סייע/ת אישית',
  'אחות',
  'פסיכולוג/ית',
  'יועץ/ת חינוכי/ת',
  'קלינאי/ת תקשורת',
  'מרפא/ה בעיסוק',
  'פיזיותרפיסט/ית',
  'מטפל/ת רגשי/ת',
  'אב/אם בית',
  'מלווה בהסעה'
];

/**
 * Fetches absence reasons from Firestore. If the collection is empty or fails,
 * falls back to DEFAULT_ABSENCE_REASONS and automatically attempts to seed Firestore.
 */
export async function getAbsenceReasons(forceRefresh = false): Promise<string[]> {
  try {
    const q = query(collection(db, 'absenceReasons'), orderBy('name'));
    const docs = await getCachedQuery<{ id?: string; name?: string; reason?: string; title?: string }>('absence_reasons', q, forceRefresh);
    if (docs && docs.length > 0) {
      const validNames = docs
        .map(d => (d.name || d.reason || d.title || '').trim())
        .filter(Boolean);
      
      if (validNames.length > 0) {
        return Array.from(new Set(validNames));
      }
    }
    
    // If empty in Firestore, trigger background seeding and return defaults
    seedDefaultAbsenceReasons().catch(err => console.warn('Could not auto-seed absence reasons:', err));
    return DEFAULT_ABSENCE_REASONS;
  } catch (err) {
    console.warn('Could not fetch absence reasons, using defaults:', err);
    return DEFAULT_ABSENCE_REASONS;
  }
}

/**
 * Seeds standard default absence reasons into Firestore.
 */
export async function seedDefaultAbsenceReasons(): Promise<number> {
  let seededCount = 0;
  try {
    const existing = await getDocs(collection(db, 'absenceReasons'));
    const existingNames = new Set(
      existing.docs
        .map(d => {
          const data = d.data();
          return (data.name || data.reason || data.title || '').trim().toLowerCase();
        })
        .filter(Boolean)
    );

    for (const reason of DEFAULT_ABSENCE_REASONS) {
      if (!existingNames.has(reason.trim().toLowerCase())) {
        await addDoc(collection(db, 'absenceReasons'), {
          name: reason.trim(),
          createdAt: new Date().toISOString()
        });
        seededCount++;
      }
    }
    clearDataCache();
  } catch (error) {
    console.error('Error seeding default absence reasons:', error);
  }
  return seededCount;
}

/**
 * Force resets or repairs the absence reasons collection, ensuring all default reasons are populated.
 */
export async function forceResetDefaultAbsenceReasons(): Promise<number> {
  let count = 0;
  try {
    count = await seedDefaultAbsenceReasons();
    clearDataCache();
  } catch (error) {
    console.error('Error force resetting default absence reasons:', error);
  }
  return count;
}

/**
 * Seeds standard default staff roles into Firestore.
 */
export async function seedDefaultStaffRoles(): Promise<number> {
  let seededCount = 0;
  try {
    const existing = await getDocs(collection(db, 'staffRoles'));
    const existingNames = new Set(existing.docs.map(d => (d.data().name || '').trim().toLowerCase()));

    for (const role of DEFAULT_STAFF_ROLES) {
      if (!existingNames.has(role.trim().toLowerCase())) {
        await addDoc(collection(db, 'staffRoles'), {
          name: role,
          createdAt: new Date().toISOString()
        });
        seededCount++;
      }
    }
    clearDataCache();
  } catch (error) {
    console.error('Error seeding default staff roles:', error);
  }
  return seededCount;
}
